/**
 * <DailyPlanDialog /> — Header-level "Daily Plan" popup (R64.3).
 *
 * A large modal (max-w-[95vw]) that visualises time allocation across
 * a workspace. Three view modes:
 *   • Day    — full hourly swimlane (rows = people, columns = 07:00-21:00)
 *   • Week   — 7 vertically-stacked mini swimlanes, one per day
 *   • Month  — calendar-style heatmap: rows = people, columns = days,
 *              each cell shows a compact stacked bar of hours by state
 *
 * Filters (multi-select): Department, Client, Brand, Project, Assignee.
 * State colour-coded (planned/submitted/approved/rejected).
 *
 * Permission model: everyone can open; non-admins see only their own
 * row (enforced by the backend).
 */
import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { localWallClockIso } from "@/lib/timeSubmit";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import MultiSelect from "@/components/ui/multi-select";
import { Calendar, ChevronLeft, ChevronRight, Filter, X, ZoomIn, ZoomOut, LayoutGrid, Rows, Plus, Search, ExternalLink, Maximize2, Minimize2, Star } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { STATE_BLOCK, STATE_LABEL, STATE_ORDER, STATE_CHIP } from "@/components/timesheet/stateColors";
import EntryReviewButton from "@/components/timesheet/EntryReviewButton";
import { useEffortFormat } from "@/lib/effortFormat";
import CheckInMarkerRow, { NowLine } from "@/components/timesheet/CheckInMarkerRow";
// Feb 2026 R291r perf · Code-split the Month heatmap renderer. It
// only runs when the user picks the Month tab in the dialog, so the
// first paint of Day/Week/List never pays for its ~140 lines + the
// HoverCard tree they bring in.
const MonthHeatmap = lazy(() => import("@/components/timesheet/DailyPlanMonthHeatmap"));
// Feb 2026 R291s perf · ListView (+ MoveEntriesDialog + ListRow,
// ~710 lines together) code-split into its own chunk. Day / Week /
// Month tabs render without ever downloading it.
const ListView = lazy(() => import("@/components/timesheet/DailyPlanListView").then((m) => ({ default: m.ListView })));
import useWorkspacePerms from "@/lib/useWorkspacePerms";

const START_HOUR = 7;
const END_HOUR   = 21;
const HOURS_SPAN = END_HOUR - START_HOUR;

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays  = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const dayOfWeek = (iso) => new Date(iso + "T00:00:00Z").getUTCDay(); // 0=Sun
const dowShort  = (iso) => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dayOfWeek(iso)];
const startOfWeek = (iso) => addDays(iso, -dayOfWeek(iso));   // start Sunday
const startOfMonth = (iso) => iso.slice(0, 7) + "-01";
const daysInMonth = (iso) => {
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};

function parseHM(t) {
  if (!t) return null;
  const [h, m] = t.split(":");
  return Number(h) + Number(m) / 60;
}

// Feb 2026 · Shift-aware capacity helpers
//   `shiftBounds(shift)` → { s, e } clamped to the Day grid (07–21),
//   returns null when the shift is missing or lies entirely outside the
//   visible window.  `isHourInShift(h, shift)` is used by the hourly
//   grid to decide shading (green inside, dimmed outside).
//   `isBlockOutsideShift(entry, shift)` flags any planned block whose
//   span strays beyond the shift so the UI can outline it in amber.
function shiftBounds(shift) {
  if (!shift) return null;
  const s = parseHM(shift.start_time);
  const e = parseHM(shift.end_time);
  if (s == null || e == null || e <= s) return null;
  const cs = Math.max(START_HOUR, s);
  const ce = Math.min(END_HOUR, e);
  if (ce <= cs) return null;
  return { s, e, cs, ce };
}
function isHourInShift(h, shift) {
  const b = shiftBounds(shift);
  if (!b) return true;  // no shift defined → don't dim anything
  return (h + 1) > b.s && h < b.e;
}
function isBlockOutsideShift(entry, shift) {
  const b = shiftBounds(shift);
  if (!b) return false;
  const s = parseHM(entry.start_time);
  const e = parseHM(entry.end_time);
  if (s == null || e == null) return false;
  return s < b.s || e > b.e;
}

// R138az · Feb 2026 — Long-form date label for hover cards & header cells.
// Accepts "YYYY-MM-DD"; returns "Mon 10 Aug 2026" format for max clarity.
function formatDateLong(dateStr) {
  if (!dateStr) return "";
  try {
    const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return dt.toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}


// Feb 2026 R291q · Module-scoped cache for the Daily Plan dialog's
// static dict lists (members / departments / clients / brands /
// projects). Keyed by workspace id; TTL 60 s. Keeps the popup snappy
// across repeated opens.
const _dpDictCache = {};


export default function DailyPlanDialog({ open, onClose }) {
  const { activeWorkspaceId: wsId, user } = useAuth();
  const { formatHours } = useEffortFormat();
  const [view, setView] = useState("day"); // day | week | month | list
  const [anchor, setAnchor] = useState(todayISO());
  // R64.4 · Zoom (font-size + row height scale) and layout toggle
  // (people-on-rows vs people-on-columns for the Day view).
  const [zoom, setZoom] = useState(1);           // 0.75 .. 1.5
  const [layout, setLayout] = useState("rows");  // rows | columns
  // R148g · Feb 2026 — density controls how tall a timeline block is
  // and how many lines the task title can wrap to. Persisted so users
  // don't have to re-pick every open.
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem("acq_daily_plan_density") || "normal"; } catch { return "normal"; }
  });
  useEffect(() => {
    try { localStorage.setItem("acq_daily_plan_density", density); } catch { /* noop */ }
  }, [density]);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // Feb 2026 · Full-screen toggle for dense timesheet review sessions.
  const [fullscreen, setFullscreen] = useState(false);
  // R138j · Feb 2026 — Filters collapse on mobile so the swimlanes get
  // more vertical room. Default: collapsed on phones, open on desktop.
  const [filtersOpen, setFiltersOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 640 : true);
  // Feb 2026 · List View respects an independent range selector so
  // managers can scan today / week / month / a custom span without
  // moving the Timeline anchor. Timeline views keep their own range
  // derived from `view` + `anchor` (below).
  const [listRange, setListRange] = useState("day"); // day | week | month | custom
  const [customFrom, setCustomFrom] = useState(todayISO());
  const [customTo,   setCustomTo]   = useState(todayISO());
  // R104 · Filter state migrated from scalars to string arrays for real
  // multi-pick. When a filter has ≥2 picks we skip the server-side param
  // and filter client-side; single picks still hit the server for speed.
  const [filters, setFilters] = useState({
    department: [],
    client_id:  [],
    brand_id:   [],
    project_id: [],
    assignee_id:[],
  });
  // R104 · Free-text search across visible lanes (client, brand, project,
  // task, comment, reviewer note, assignee, designation, department).
  const [searchQ, setSearchQ] = useState("");
  // Feb 2026 · R293 — Click a time block to "pin" its hover-card open so
  // the user can read the full detail without the card closing on
  // mouse-leave. Clicking the same block again clears it; clicking any
  // other block switches the pin to that one.
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  // Clear the pin when the user navigates to another date/view so a
  // block pinned on Monday doesn't quietly stay "open" on Tuesday.
  useEffect(() => { setSelectedEntryId(null); }, [view, anchor]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Filter option lists
  const [members, setMembers]   = useState([]);
  const [departments, setDepts] = useState([]);
  const [clients, setClients]   = useState([]);
  const [brands, setBrands]     = useState([]);
  const [projects, setProjects] = useState([]);

  // ── Date range derived from view + anchor ────────────────────────────
  const range = useMemo(() => {
    // Feb 2026 · List view uses its own range selector so it can span
    // today/week/month/custom independently of the timeline anchor.
    if (view === "list") {
      if (listRange === "day") return { from: anchor, to: anchor };
      if (listRange === "week") {
        const from = startOfWeek(anchor);
        return { from, to: addDays(from, 6) };
      }
      if (listRange === "month") {
        const from = startOfMonth(anchor);
        const days = daysInMonth(anchor);
        return { from, to: addDays(from, days - 1) };
      }
      // custom
      const from = customFrom <= customTo ? customFrom : customTo;
      const to   = customFrom <= customTo ? customTo : customFrom;
      return { from, to };
    }
    if (view === "day") return { from: anchor, to: anchor };
    if (view === "week") {
      const from = startOfWeek(anchor);
      return { from, to: addDays(from, 6) };
    }
    const from = startOfMonth(anchor);
    const days = daysInMonth(anchor);
    return { from, to: addDays(from, days - 1) };
  }, [view, anchor, listRange, customFrom, customTo]);

  // ── Load filter option lists once per open ──────────────────────────
  // Feb 2026 R291q perf · Module-scoped cache for the four dict lists
  // (members / departments / clients / brands / projects). The daily-
  // plan popup is opened dozens of times a day; these lists are near-
  // static. 60-second TTL per workspace keeps re-opens instant without
  // caching stale project moves. Projects limit halved to 200 — the
  // picker shows a search box so the full list is not visible anyway.
  useEffect(() => {
    if (!open || !wsId) return;
    let cancelled = false;
    (async () => {
      const cached = _dpDictCache[wsId];
      if (cached && (Date.now() - cached.at) < 60_000) {
        setMembers(cached.mem); setDepts(cached.dep);
        setClients(cached.cli); setBrands(cached.brd);
        setProjects(cached.prj);
        return;
      }
      const safe = (p) => p.then((r) => r.data || []).catch(() => []);
      const [mem, dep, cli, brd, prj] = await Promise.all([
        safe(api.get(`/workspaces/${wsId}/members`)),
        safe(api.get(`/workspaces/${wsId}/departments`)),
        safe(api.get(`/workspaces/${wsId}/clients`)),
        safe(api.get(`/workspaces/${wsId}/brands`)),
        safe(api.get(`/workspaces/${wsId}/projects`, { params: { limit: 200 } })),
      ]);
      if (cancelled) return;
      const _mem = Array.isArray(mem) ? mem : (mem.rows || mem.items || []);
      const _dep = Array.isArray(dep) ? dep : (dep.rows || dep.items || []);
      const _cli = Array.isArray(cli) ? cli : (cli.rows || cli.items || []);
      const _brd = Array.isArray(brd) ? brd : (brd.rows || brd.items || []);
      const _prj = Array.isArray(prj) ? prj : (prj.rows || prj.items || []);
      setMembers(_mem); setDepts(_dep); setClients(_cli);
      setBrands(_brd); setProjects(_prj);
      _dpDictCache[wsId] = { at: Date.now(), mem: _mem, dep: _dep, cli: _cli, brd: _brd, prj: _prj };
    })();
    return () => { cancelled = true; };
  }, [open, wsId]);

  // ── Load plan-view data on any dep change ───────────────────────────
  const { hasPerm } = useWorkspacePerms();
  const canSeeCheckins = hasPerm("attendance.view_on_daily_plan")
                       || hasPerm("attendance.view_all")
                       || hasPerm("workspace.admin")
                       || hasPerm("workspace.manage")
                       || hasPerm("hr.approve");
  const [checkinMarkers, setCheckinMarkers] = useState({});
  useEffect(() => {
    if (!open || !wsId || !canSeeCheckins) { setCheckinMarkers({}); return; }
    // Only load markers when viewing a single day (day view or list day scope).
    if (view !== "day") return;
    let cancelled = false;
    api.get(`/workspaces/${wsId}/attendance/check-in-markers`,
             { params: { date: range.from } })
       .then((r) => {
         if (cancelled) return;
         const byUid = {};
         for (const m of (r.data?.markers || [])) {
           if (m.user_id) byUid[m.user_id] = m;
         }
         setCheckinMarkers(byUid);
       })
       .catch(() => { if (!cancelled) setCheckinMarkers({}); });
    return () => { cancelled = true; };
  }, [open, wsId, view, range.from, canSeeCheckins]);

  useEffect(() => {
    if (!open || !wsId) return;
    let cancelled = false;
    setLoading(true);
    const params = { from: range.from, to: range.to };
    // R104 · Send single-pick filters only. Multi-picks are filtered
    // client-side (see `matchesFilters` below).
    for (const [k, v] of Object.entries(filters)) {
      if (Array.isArray(v) && v.length === 1) params[k] = v[0];
    }
    api.get(`/workspaces/${wsId}/timesheet/plan-view`, { params })
       .then((r) => { if (!cancelled) setData(r.data); })
       .catch(() => { if (!cancelled) setData({ lanes: [], is_admin: false }); })
       .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Oct 2026 · user?.id in deps so impersonation (admin → user) always
    // refetches instead of flashing the admin's last payload before the
    // new viewer's data lands.
  }, [open, wsId, range.from, range.to, filters, user?.id]);

  const brandsForClient = useMemo(() => {
    if (filters.client_id.length !== 1) return brands;
    return brands.filter((b) => b.client_id === filters.client_id[0]);
  }, [brands, filters.client_id]);

  const projectsForFilters = useMemo(() => {
    return projects.filter((p) => {
      if (filters.client_id.length && !filters.client_id.includes(p.client_id)) return false;
      if (filters.brand_id.length && !filters.brand_id.includes(p.brand_id)) return false;
      return true;
    });
  }, [projects, filters.client_id, filters.brand_id]);

  const clearFilters = () => { setFilters({
    department: [], client_id: [], brand_id: [], project_id: [], assignee_id: [],
  }); setSearchQ(""); };

  const anyFilterOn = Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : !!v)) || !!searchQ;
  const isAdmin = data?.is_admin;

  // R104 · Bump the fetch dep so we re-hit the server, and fire a global
  // `timesheet:changed` event so the parent Timesheet popup (or any other
  // subscriber) can refetch. Bridges the sync gap the user reported —
  // blocks added inside Daily Plan now show up on the task row without a
  // manual refresh.
  const reload = () => {
    setFilters((f) => ({ ...f }));
    try {
      window.dispatchEvent(new CustomEvent("timesheet:changed", {
        detail: { source: "daily-plan", date: range.from, ws_id: wsId },
      }));
    } catch { /* SSR / older jsdom — ignore */ }
  };

  // R104 · Client-side filter for multi-picks + global text search.
  // Applied to the data returned by the server (which honoured only
  // single-pick params). Also drives the new List View. Lanes represent
  // {user_id, name, entries:[…]}; we deep-filter entries and drop lanes
  // that end up empty.
  //
  // Feb 2026 · R293 — Also hide lanes for employees whose `exit_date`
  // has already passed on the viewed date (ex-employees still carry
  // historical entries so the backend keeps the lane, but a date in the
  // future shouldn't show them as idle).
  const filteredLanes = useMemo(() => {
    const lanes = data?.lanes || [];
    const multi = (arr) => Array.isArray(arr) && arr.length > 1;
    const q = (searchQ || "").trim().toLowerCase();
    const effectiveDate = view === "day" ? anchor : null;
    const laneOk = (l) => {
      if (multi(filters.assignee_id) && !filters.assignee_id.includes(l.user_id)) return false;
      if (multi(filters.department)) {
        if (!filters.department.includes(l.department || l.department_name)) return false;
      }
      // Feb 2026 · Hide employees inactive on the viewed date (Day view
      // only — Week/Month aggregates and historical data can still show
      // them).
      if (effectiveDate && l.exit_date && l.exit_date < effectiveDate) return false;
      return true;
    };
    // Feb 2026 · R293 — When the user types a name, match on
    // `lane.user_name` so the whole lane stays visible even if that
    // person has no entries matching the search text (previously the
    // lane got dropped because entries' assignee_name is sometimes
    // blank for synthetic leave/WFH rows).
    const laneMatchesSearch = (l) => {
      if (!q) return false;
      return (l.user_name || "").toLowerCase().includes(q);
    };
    const entryOk = (e) => {
      if (multi(filters.client_id) && !filters.client_id.includes(e.client_id)) return false;
      if (multi(filters.brand_id) && !filters.brand_id.includes(e.brand_id)) return false;
      if (multi(filters.project_id) && !filters.project_id.includes(e.project_id)) return false;
      if (q) {
        const hay = [
          e.project_name, e.task_title, e.brand_name, e.client_name,
          e.notes, e.reviewer_comment, e.review_comment, e.assignee_name,
          e.designation, e.department,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    };
    return lanes
      .filter(laneOk)
      .map((l) => {
        // If the lane name itself matches the search, keep every entry
        // (unchanged by text) so the user can still see what the
        // person is working on. Multi-pick filters still apply.
        if (laneMatchesSearch(l)) {
          const entries = (l.entries || []).filter((e) => {
            if (multi(filters.client_id) && !filters.client_id.includes(e.client_id)) return false;
            if (multi(filters.brand_id) && !filters.brand_id.includes(e.brand_id)) return false;
            if (multi(filters.project_id) && !filters.project_id.includes(e.project_id)) return false;
            return true;
          });
          return { ...l, entries };
        }
        return { ...l, entries: (l.entries || []).filter(entryOk) };
      })
      .filter((l) => {
        // Keep a lane with no entries only when the search text matched
        // its name directly (user wants to see the person is on nothing
        // right now) or when no filter is active at all.
        const hasEntries = (l.entries || []).length > 0;
        if (hasEntries) return true;
        if (q) return laneMatchesSearch(l);
        return !multi(filters.client_id) && !multi(filters.brand_id) && !multi(filters.project_id);
      });
  }, [data, filters, searchQ, view, anchor]);

  const shiftAnchor = (n) => {
    // Feb 2026 · When in List mode, respect the list-range selector for
    // navigation (day / week / month steps). Custom range never shifts.
    if (view === "list") {
      if (listRange === "day")   setAnchor(addDays(anchor, n));
      else if (listRange === "week") setAnchor(addDays(anchor, 7 * n));
      else if (listRange === "month") {
        const [y, m] = anchor.split("-").map(Number);
        const nd = new Date(Date.UTC(y, m - 1 + n, 1));
        setAnchor(nd.toISOString().slice(0, 10));
      }
      return;
    }
    if (view === "day")   setAnchor(addDays(anchor, n));
    else if (view === "week") setAnchor(addDays(anchor, 7 * n));
    else                  {
      const [y, m] = anchor.split("-").map(Number);
      const nd = new Date(Date.UTC(y, m - 1 + n, 1));
      setAnchor(nd.toISOString().slice(0, 10));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className={`${fullscreen
          ? "max-w-[100vw] w-[100vw] max-h-[100dvh] h-[100dvh] sm:max-w-[98vw] sm:w-[98vw] sm:max-h-[98vh] sm:h-[98vh]"
          : "max-w-[100vw] w-[100vw] max-h-[100dvh] h-[100dvh] sm:max-w-[95vw] sm:w-[95vw] sm:max-h-[92vh] sm:h-[92vh]"
        } overflow-hidden flex flex-col p-0 rounded-none !top-4 !translate-y-0`}
                     data-testid="daily-plan-dialog">
        <DialogHeader className="px-4 pt-3 pb-2 border-b border-border flex-row items-center gap-3 space-y-0 pr-20">
          <Calendar className="w-5 h-5 text-primary shrink-0" />
          <DialogTitle className="text-lg">Daily plan · time allocation</DialogTitle>
          <span className="text-xs text-muted-foreground truncate">
            {isAdmin ? "Workspace view" : "You only (needs manager permission for workspace view)"}
          </span>
          {/* Feb 2026 · R293 — Expand button pinned next to Radix' built-in
              X close (which sits at `top-4 right-4`). Previously we had
              `ml-auto` which pushed it to the end of the flex row,
              leaving a visible gap between it and the X. Absolute
              positioning at `right-11` places them side-by-side with
              one icon-width (36px) between their centres. */}
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 rounded-none absolute top-3 right-11 shrink-0"
            onClick={() => setFullscreen((v) => !v)}
            title={fullscreen ? "Exit full screen" : "Full screen"}
            data-testid="daily-plan-fullscreen-toggle"
          >
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </DialogHeader>
        {/* R64.4 · Second toolbar row — moved OUT of DialogHeader so the built-in
             Radix X close button (top-right, absolute) never overlaps controls.
             `pr-12` on the header row above reserves 48px for the X. */}
        <div className="px-4 py-2 border-b border-border bg-muted/20 flex items-center gap-2 flex-wrap" data-testid="daily-plan-toolbar">
          {/* Feb 2026 · Two-level view selector: Timeline (Day/Week/Month) vs List. */}
          <div className="inline-flex items-center border border-border rounded-none">
            <button
              type="button"
              onClick={() => { if (view === "list") setView("day"); }}
              className={`h-8 px-3 text-xs border-r border-border ${view !== "list" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="daily-plan-mode-timeline"
            >Timeline</button>
            <button
              type="button"
              onClick={() => setView("list")}
              className={`h-8 px-3 text-xs ${view === "list" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="daily-plan-mode-list"
            >List</button>
          </div>
          {view !== "list" && (
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="rounded-none h-8">
                <TabsTrigger value="day"   className="rounded-none text-xs" data-testid="daily-plan-view-day">Day</TabsTrigger>
                <TabsTrigger value="week"  className="rounded-none text-xs" data-testid="daily-plan-view-week">Week</TabsTrigger>
                <TabsTrigger value="month" className="rounded-none text-xs" data-testid="daily-plan-view-month">Month</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
          {view === "list" && (
            <div className="inline-flex items-center border border-border rounded-none" data-testid="daily-plan-list-range">
              {[
                { k: "day",    label: "Today"     },
                { k: "week",   label: "This week" },
                { k: "month",  label: "This month"},
                { k: "custom", label: "Custom"    },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setListRange(o.k)}
                  className={`h-8 px-2.5 text-[11px] border-r border-border last:border-r-0 ${listRange === o.k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
                  data-testid={`daily-plan-list-range-${o.k}`}
                >{o.label}</button>
              ))}
            </div>
          )}
          {view === "list" && listRange === "custom" ? (
            <div className="inline-flex items-center gap-1" data-testid="daily-plan-list-custom">
              <Input type="date" value={customFrom}
                     onChange={(e) => setCustomFrom(e.target.value)}
                     className="h-8 w-36 rounded-none text-xs"
                     data-testid="daily-plan-list-custom-from" />
              <span className="text-[10px] text-muted-foreground">→</span>
              <Input type="date" value={customTo}
                     onChange={(e) => setCustomTo(e.target.value)}
                     className="h-8 w-36 rounded-none text-xs"
                     data-testid="daily-plan-list-custom-to" />
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 border border-border rounded-none">
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none" onClick={() => shiftAnchor(-1)} data-testid="daily-plan-prev"><ChevronLeft className="w-4 h-4" /></Button>
              {/* Feb 2026 · Day view — quick jump-to-date picker so users
                  don't have to chevron through weeks one day at a time.
                  Week/Month views keep the read-only range chip since a
                  full date picker there would be ambiguous. */}
              {view === "day" || (view === "list" && listRange === "day") ? (
                <input
                  type="date"
                  value={anchor}
                  onChange={(e) => e.target.value && setAnchor(e.target.value)}
                  className="h-8 border-0 bg-transparent text-xs font-mono px-1 tabular-nums focus:outline-none focus:ring-1 focus:ring-primary/40 cursor-pointer"
                  data-testid="daily-plan-date-jump"
                  title="Jump to a specific date"
                />
              ) : (
                <span className="text-xs font-mono px-2 tabular-nums" data-testid="daily-plan-range">
                  {range.from === range.to ? range.from : `${range.from} → ${range.to}`}
                </span>
              )}
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none" onClick={() => shiftAnchor(1)} data-testid="daily-plan-next"><ChevronRight className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" className="h-8 rounded-none text-[11px]" onClick={() => setAnchor(todayISO())} data-testid="daily-plan-today">Today</Button>
            </div>
          )}
          <div className="inline-flex items-center gap-1 border border-border rounded-none">
            {/* R103 · Zoom → font stepper. R111 · Uses CSS `zoom` so
                the entire dialog body (all Tailwind text-* classes are
                rem-absolute and don't scale with a parent `em`) resizes
                consistently. Boundaries clamped 60% .. 200%; buttons
                auto-disable at each end so users get feedback. */}
            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-[13px] disabled:opacity-40"
                    disabled={zoom <= 0.6}
                    onClick={() => setZoom((z) => Math.max(0.6, +(z - 0.1).toFixed(2)))}
                    title={zoom <= 0.6 ? "At minimum font size" : "Decrease font size"}
                    data-testid="daily-plan-font-smaller">A-</Button>
            <span className="text-[10px] font-mono px-1 tabular-nums w-9 text-center" data-testid="daily-plan-zoom-level">{Math.round(zoom * 100)}%</span>
            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-none text-[15px] font-semibold disabled:opacity-40"
                    disabled={zoom >= 2.0}
                    onClick={() => setZoom((z) => Math.min(2.0, +(z + 0.1).toFixed(2)))}
                    title={zoom >= 2.0 ? "At maximum font size" : "Increase font size"}
                    data-testid="daily-plan-font-larger">A+</Button>
            {zoom !== 1 && (
              <Button size="sm" variant="ghost" className="h-8 rounded-none text-[10px] px-1.5"
                      onClick={() => setZoom(1)}
                      title="Reset font size"
                      data-testid="daily-plan-font-reset">Reset</Button>
            )}
          </div>
          {view === "day" && (
            <div className="flex items-center gap-1" data-testid="daily-plan-density-group" title="Timeline block height / lines of text">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground pr-1">Density</span>
              {[
                { v: "compact", label: "Compact" },
                { v: "normal", label: "Normal" },
                { v: "comfortable", label: "Comfortable" },
              ].map((d) => (
                <Button
                  key={d.v}
                  size="sm"
                  variant={density === d.v ? "default" : "outline"}
                  className="h-8 rounded-none text-[10px] px-2"
                  onClick={() => setDensity(d.v)}
                  data-testid={`daily-plan-density-${d.v}`}
                >
                  {d.label}
                </Button>
              ))}
            </div>
          )}
          {view === "day" && (
            <Button size="sm" variant="outline" className="h-8 rounded-none text-[11px]"
                    onClick={() => setLayout((l) => l === "rows" ? "columns" : "rows")}
                    title="Swap layout — people on rows vs people on columns"
                    data-testid="daily-plan-layout-toggle">
              {layout === "rows" ? <><LayoutGrid className="w-3 h-3 mr-1" />Cols</> : <><Rows className="w-3 h-3 mr-1" />Rows</>}
            </Button>
          )}
          <Button size="sm" className="h-8 rounded-none text-[11px] ml-auto"
                  onClick={() => setQuickAddOpen(true)}
                  data-testid="daily-plan-quick-add-btn"
                  title="Quickly plan a new time block (double-click a swimlane row also works)">
            <Plus className="w-3 h-3 mr-1" />Quick add
          </Button>
        </div>

        {/* Filters — collapsible on mobile (R138j · Feb 2026) */}
        <div className="border-b border-border bg-muted/40" data-testid="daily-plan-filters-wrap">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground hover:bg-muted/60"
            data-testid="daily-plan-filters-toggle"
          >
            <span className="inline-flex items-center gap-2">
              <Filter className="w-3.5 h-3.5" /> Filters
              {anyFilterOn && <span className="text-primary normal-case tracking-normal">· active</span>}
            </span>
            <span className="text-xs">{filtersOpen ? "▲" : "▼"}</span>
          </button>
          {filtersOpen && (
          <div className="px-4 py-2 flex flex-wrap items-center gap-2" data-testid="daily-plan-filters">
          <MSFilter placeholder="Department" value={filters.department}
                    options={departments.map((d) => ({ value: d.name, label: d.name }))}
                    onChange={(v) => setFilters({ ...filters, department: v })}
                    testid="daily-plan-filter-department" />
          <MSFilter placeholder="Client" value={filters.client_id}
                    options={clients.map((c) => ({ value: c.id, label: c.name }))}
                    onChange={(v) => setFilters({ ...filters, client_id: v, brand_id: [], project_id: [] })}
                    testid="daily-plan-filter-client" />
          <MSFilter placeholder="Brand" value={filters.brand_id}
                    options={brandsForClient.map((b) => ({ value: b.id, label: b.name }))}
                    onChange={(v) => setFilters({ ...filters, brand_id: v, project_id: [] })}
                    testid="daily-plan-filter-brand" />
          <MSFilter placeholder="Project" value={filters.project_id}
                    options={projectsForFilters.map((p) => ({ value: p.id, label: p.name }))}
                    onChange={(v) => setFilters({ ...filters, project_id: v })}
                    testid="daily-plan-filter-project" />
          <MSFilter placeholder="Assignee" value={filters.assignee_id}
                    options={members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email || m.user_id }))}
                    onChange={(v) => setFilters({ ...filters, assignee_id: v })}
                    testid="daily-plan-filter-assignee" />
          {/* R104 · Global free-text search across every visible field. */}
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search employee, project, task, comment, brand…"
            className="h-8 w-full sm:w-64 px-2 text-[11px] border border-border bg-background rounded-none focus:outline-none focus:border-primary"
            data-testid="daily-plan-search"
          />
          {anyFilterOn && (
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground rounded-none" onClick={clearFilters} data-testid="daily-plan-clear-filters">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-3 text-[11px] text-muted-foreground">
            <span>Total <b className="text-foreground font-mono" data-testid="daily-plan-header-total">{formatHours(
              (data?.lanes || []).reduce((s, l) => s + (l.entries || []).reduce((a, e) => a + (Number(e.hours) || 0), 0), 0),
              { unit: true },
            )}</b></span>
            <StateLegend />
          </div>
          </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-auto" data-testid="daily-plan-body">
          <div style={{ zoom: zoom }} data-testid="daily-plan-body-zoom">
          {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && (data?.lanes || []).length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground italic">
              No entries in this range. Change the date, view, or filters.
            </div>
          )}
          {!loading && (data?.lanes || []).length > 0 && (
            <>
              {view === "day"   && <DaySwimlane   lanes={filteredLanes} date={range.from} layout={layout} density={density} onDoubleAdd={(preset) => setQuickAddOpen(preset || true)} canReview={data.can_review} onReviewed={reload} checkinMarkersByUser={checkinMarkers}
                                                   selectedEntryId={selectedEntryId} onSelectEntry={setSelectedEntryId} />}
              {view === "week"  && <WeekView       lanes={filteredLanes} from={range.from} to={range.to} currentUserId={user?.id} canReview={data.can_review} onReviewed={reload} />}
              {view === "month" && (
                <Suspense fallback={<div className="text-xs text-muted-foreground italic py-6 text-center animate-pulse">Loading month view…</div>}>
                  <MonthHeatmap  lanes={filteredLanes} from={range.from} to={range.to} currentUserId={user?.id} />
                </Suspense>
              )}
              {view === "list"  && (
                <Suspense fallback={<div className="text-xs text-muted-foreground italic py-6 text-center animate-pulse">Loading list view…</div>}>
                  <ListView      lanes={filteredLanes} canReview={data.can_review} onReviewed={reload}
                                                              onNudgeIdle={async (uids) => {
                                                                if (!uids?.length) return;
                                                                try {
                                                                  const { data: r } = await api.post(`/workspaces/${wsId}/timesheet-plan/nudge-idle`, {
                                                                    date: range.from,
                                                                    user_ids: uids,
                                                                  });
                                                                  toast.success(r?.message || `Nudged ${r?.nudged || 0} teammate${(r?.nudged || 0) === 1 ? "" : "s"}`);
                                                                } catch (e) {
                                                                  toast.error(apiErrorMessage(e, "Nudge failed"));
                                                                }
                                                              }} />
                </Suspense>
              )}
            </>
          )}
          </div>
        </div>
        {quickAddOpen && (
          <QuickAddPlanBlock
            wsId={wsId}
            initialDate={range.from}
            initialTaskId={typeof quickAddOpen === "object" ? quickAddOpen.taskId : undefined}
            initialStart={typeof quickAddOpen === "object" ? quickAddOpen.start : undefined}
            onClose={() => setQuickAddOpen(false)}
            onAdded={() => { setQuickAddOpen(false); reload(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Small utility components ─────────────────────────────────────────

// R104 · Multi-select filter used across the Daily Plan toolbar. Thin
// wrapper around `MultiSelect` that fixes the trigger width and label
// suffix so the popup behaves consistently for every dimension.
function MSFilter({ placeholder, value, options, onChange, testid }) {
  return (
    <MultiSelect
      options={options}
      value={value || []}
      onChange={onChange}
      placeholder={placeholder}
      allLabel={`All ${placeholder.toLowerCase()}s`}
      maxChips={1}
      width="w-44"
      testid={testid}
    />
  );
}

// Kept for backward compat with any external callers.
function FilterSelect({ placeholder, value, options, onChange, testid }) {
  const opts = (options || []).map((o) => ({
    value: o.id || o.value, label: o.name || o.label || (o.id || o.value),
  }));
  return (
    <MultiSelect
      options={opts}
      value={value ? [value] : []}
      onChange={(arr) => onChange(arr.length ? arr[arr.length - 1] : "")}
      placeholder={placeholder}
      allLabel={`All ${placeholder.toLowerCase()}s`}
      maxChips={1}
      width="w-44"
      testid={testid}
    />
  );
}

function StateLegend() {
  return (
    <span className="inline-flex items-center gap-2" data-testid="daily-plan-legend">
      {STATE_ORDER.map((s) => (
        <span key={s} className="inline-flex items-center gap-1">
          <span className={`w-2.5 h-2.5 ${STATE_BLOCK[s].bg}`} />
          <span>{STATE_LABEL[s]}</span>
        </span>
      ))}
    </span>
  );
}

// ── DAY view: full hourly swimlane ───────────────────────────────────

function DaySwimlane({ lanes, date, layout = "rows", density = "normal", onDoubleAdd, canReview, onReviewed, checkinMarkersByUser = null, selectedEntryId = null, onSelectEntry = null }) {
  const hourCols = useMemo(
    () => Array.from({ length: HOURS_SPAN }, (_, i) => START_HOUR + i),
    [],
  );
  if (layout === "columns") {
    return <DaySwimlaneTransposed lanes={lanes} date={date} hours={hourCols} density={density} onDoubleAdd={onDoubleAdd} canReview={canReview} onReviewed={onReviewed} selectedEntryId={selectedEntryId} onSelectEntry={onSelectEntry} />;
  }
  return (
    <div className="min-w-[900px] relative" data-testid="daily-plan-day-swimlane">
      <div className="grid sticky top-0 z-10 bg-background" style={{ gridTemplateColumns: `180px repeat(${HOURS_SPAN}, minmax(60px, 1fr))` }}>
        <div className="border-b border-border/70 bg-primary/10 border-l-4 border-l-primary px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-primary"
             data-testid="daily-plan-header-cell">
          Person · {date} · {formatDateLong(date)}
        </div>
        {hourCols.map((h) => (
          <div key={h}
               className="relative border-b border-border/70 bg-secondary/40 px-2 py-2 text-[10px] uppercase tracking-wider border-l-2 border-l-foreground/70">
            {/* Feb 2026 · R293 — Hour label left-aligned to the actual
                hour tick (previously right-aligned, which placed the
                number at the END of its 1-hour cell — visually over the
                NEXT hour's start). Bold weight so hours anchor the
                eye. */}
            <span className="font-bold text-foreground">{String(h).padStart(2, "0")}:00</span>
            {/* Clock-tick heights for the sub-hour markers so the
                header reads like a wristwatch face:
                  hour  → full border-l (above, 2px bold)
                  30min → half-height, 2px bold
                  15/45 → quarter-height, 1px */}
            <span className="pointer-events-none absolute top-3/4 bottom-0 left-[25%] w-px bg-foreground/50" data-testid={`daily-plan-tick-${h}-15`} />
            <span className="pointer-events-none absolute top-1/2 bottom-0 left-[50%] w-0.5 bg-foreground/70" data-testid={`daily-plan-tick-${h}-30`} />
            <span className="pointer-events-none absolute top-3/4 bottom-0 left-[75%] w-px bg-foreground/50" data-testid={`daily-plan-tick-${h}-45`} />
          </div>
        ))}
      </div>
      {lanes.map((lane) => (
        <DayLane key={lane.user_id} lane={lane} hours={hourCols} date={date} density={density} onDoubleAdd={onDoubleAdd} canReview={canReview} onReviewed={onReviewed}
                 checkinMarker={checkinMarkersByUser ? checkinMarkersByUser[lane.user_id] : null}
                 selectedEntryId={selectedEntryId} onSelectEntry={onSelectEntry} />
      ))}
      {/* Iter Feb2026 · "Now" vertical line across all lanes (only
          rendered when viewing today so the eye tracks quickly). */}
      {date === new Date().toISOString().slice(0, 10) && (
        <NowLine startHour={START_HOUR} endHour={END_HOUR} />
      )}
    </div>
  );
}

function DaySwimlaneTransposed({ lanes, date, hours, onDoubleAdd, canReview, onReviewed, density = "normal", selectedEntryId = null, onSelectEntry = null }) {
  // R148g · density → row height + block wrapping
  const cellMin = (DENSITY_BLOCK[density] || DENSITY_BLOCK.normal).cellMin;
  // Oct 2026 · Drafts (state=planned) saved without a start_time still
  // belong to the day and must surface somewhere, otherwise the dialog
  // silently drops them. Render them in a top "Untimed" row above the
  // hourly grid for this view, grouped per lane. Timed entries continue
  // to render inside the correct hour cell as before.
  const firstHour = hours[0];
  return (
    <div className="min-w-[600px] max-w-full" data-testid="daily-plan-day-swimlane-transposed">
      <div className="grid sticky top-0 z-10 bg-background border-b border-border/70" style={{ gridTemplateColumns: `100px repeat(${lanes.length}, minmax(140px, 1fr))` }}>
        <div className="bg-primary/10 border-l-4 border-l-primary px-2 py-2 text-[10px] uppercase tracking-wider font-semibold text-primary"
             data-testid="daily-plan-header-cell-transposed">
          Time · {date} · {formatDateLong(date)}
        </div>
        {lanes.map((l) => (
          <div key={l.user_id} className="bg-secondary/40 px-2 py-2 text-[10px] uppercase tracking-wider border-l border-border/40">
            <div className="truncate font-semibold">{l.user_name}</div>
            {/* Oct 2026 · Compact shift pill under the name in the
                people-on-columns view. */}
            {l.shift?.start_time && (
              <div className="normal-case font-mono text-[9px] text-muted-foreground truncate"
                   data-testid={`daily-plan-shift-col-${l.user_id}`}>
                {l.shift.start_time}–{l.shift.end_time}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Untimed drafts row — only renders when at least one lane has an
          untimed entry on this date, so the row doesn't take up space
          on clean days. */}
      {(() => {
        const untimedByLane = lanes.map((lane) =>
          (lane.entries || []).filter((e) =>
            e.date === date
            && (parseHM(e.start_time) == null || parseHM(e.end_time) == null)
          )
        );
        const anyUntimed = untimedByLane.some((arr) => arr.length > 0);
        if (!anyUntimed) return null;
        return (
          <div className="grid bg-amber-50/40" style={{ gridTemplateColumns: `100px repeat(${lanes.length}, minmax(140px, 1fr))` }}
               data-testid="daily-plan-untimed-row">
            <div className="border-b border-border/40 px-2 py-2 text-[10px] uppercase tracking-wider text-amber-900 font-semibold">
              Untimed
            </div>
            {lanes.map((lane, i) => (
              <div key={lane.user_id}
                   className={`border-b border-l border-border/40 relative py-0.5 px-0.5 ${cellMin}`}
                   data-testid={`daily-plan-tcell-untimed-${lane.user_id}`}>
                {untimedByLane[i].map((e) =>
                  <BlockChip key={e.id} entry={e} canReview={canReview} onReviewed={onReviewed} density={density} />
                )}
              </div>
            ))}
          </div>
        );
      })()}
      {hours.map((h) => (
        <div key={h} className="grid" style={{ gridTemplateColumns: `100px repeat(${lanes.length}, minmax(140px, 1fr))` }}>
          <div className="border-b border-border/40 px-2 py-2 text-[10px] font-mono text-muted-foreground">{String(h).padStart(2, "0")}:00</div>
          {lanes.map((lane) => {
            const entries = (lane.entries || []).filter((e) => {
              if (e.date !== date) return false;
              const start = parseHM(e.start_time);
              const end   = parseHM(e.end_time);
              return start != null && end != null && start < h + 1 && end > h;
            });
            // Feb 2026 · Shade the cell by shift: emerald tint inside the
            // shift window, neutral dim outside. Based on this lane's
            // own shift so swimlanes with different shifts read clearly.
            const inShift = isHourInShift(h, lane.shift);
            const bandCls = lane.shift
              ? (inShift ? "bg-emerald-50/60 dark:bg-emerald-900/15" : "bg-neutral-100/70 dark:bg-neutral-800/40")
              : "";
            return (
              <div key={lane.user_id}
                   className={`border-b border-l border-border/40 relative py-0.5 px-0.5 ${cellMin} ${bandCls}`}
                   onDoubleClick={() => onDoubleAdd?.({ userId: lane.user_id, start: `${String(h).padStart(2,"0")}:00`, taskId: null })}
                   data-testid={`daily-plan-tcell-${lane.user_id}-${h}`}>
                {entries.map((e) => {
                  const outOfShift = isBlockOutsideShift(e, lane.shift);
                  const isSelected = selectedEntryId === e.id;
                  return <BlockChip key={e.id} entry={e} canReview={canReview} onReviewed={onReviewed} density={density} outOfShift={outOfShift}
                                      isSelected={isSelected}
                                      onClick={() => onSelectEntry?.(isSelected ? null : e.id)} />;
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// R148g · Feb 2026 — density → per-block Tailwind classes + row height.
// Compact stays on a single truncated line (small blocks fit many
// entries); Normal wraps to 2 lines; Comfortable to 3.  `laneHeightPx`
// is used by the row-layout DayLane and the transposed cell.
// R157c · Feb 2026 — Actually apply density everywhere. Density was
// declared but not threaded from the parent DaySwimlane, so clicking
// the toggle silently did nothing. Fixed by (a) passing density down
// through DaySwimlane → DayLane / DaySwimlaneTransposed, and
// (b) applying `laneHeightPx` on the absolute-positioned row wrapper.
const DENSITY_BLOCK = {
  compact:     { titleCls: "truncate",       cellMin: "min-h-[28px]", laneHeightPx: 30 },
  normal:      { titleCls: "line-clamp-2 whitespace-normal leading-tight", cellMin: "min-h-[44px]", laneHeightPx: 44 },
  comfortable: { titleCls: "line-clamp-3 whitespace-normal leading-tight", cellMin: "min-h-[58px]", laneHeightPx: 62 },
};

// Compact one-line block used in the transposed layout.
function BlockChip({ entry, canReview, onReviewed, density = "compact", outOfShift = false, isSelected = false, onClick = null }) {
  const { formatHours } = useEffortFormat();
  // Feb 2026 · Double-click opens the linked task in the standard Edit
  // Task popup via `/app/tasks?task=<id>` route param (consumed by the
  // Tasks page). Transposed view uses `BlockChip`.
  const navigate = useNavigate();
  const s = STATE_BLOCK[entry.state] || STATE_BLOCK.submitted;
  // Feb 2026 R103 · Right-click on a block submits it (planned → submitted).
  // Silent no-op when the entry isn't in a submittable state so we never
  // steal the browser's native context menu unless there's real work to do.
  const canSubmit = (entry.state === "planned");
  const rightClickSubmit = async (e) => {
    if (!canSubmit) return;
    e.preventDefault();
    if (!window.confirm(`Submit ${entry.hours}h for "${entry.task_title || entry.project_name || 'entry'}"? Reviewers will be notified.`)) return;
    try {
      await api.post(`/time-entries/${entry.id}/submit`,
                              { client_now: localWallClockIso() });
      toast.success("Submitted");
      onReviewed?.();
    } catch (err) { toast.error(apiErrorMessage(err, "Failed to submit")); }
  };
  // Feb 2026 · R293 — Normal/Comfortable densities wrap the title.
  const wrap = density !== "compact";
  return (
    <EntryHoverCard entry={entry} canReview={canReview} onReviewed={onReviewed} forceOpen={isSelected}>
      <div
        className={`text-[10px] px-1.5 py-0.5 border flex gap-1 cursor-pointer ${s.bg} ${s.border} ${s.fg} ${wrap ? "items-start flex-wrap" : "items-center"} ${outOfShift ? "ring-2 ring-amber-500" : ""} ${isSelected ? "ring-2 ring-primary" : ""}`}
        data-testid={`daily-plan-tblock-${entry.id}`}
        onContextMenu={rightClickSubmit}
        onClick={(ev) => { ev.stopPropagation(); onClick?.(); }}
        onDoubleClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          if (entry.task_id) navigate(`/app/tasks?task=${encodeURIComponent(entry.task_id)}`);
        }}
        title={outOfShift ? "Outside shift window" : (entry.task_id ? "Double-click to open task" : (canSubmit ? "Right-click to submit" : ""))}
      >
        <span className={`font-medium flex-1 min-w-0 ${wrap ? "whitespace-normal break-words" : (DENSITY_BLOCK[density] || DENSITY_BLOCK.compact).titleCls}`}>
          {entry.task_title || entry.project_name || "Entry"}
        </span>
        {entry.rating > 0 && (
          <span className="text-amber-200 font-mono shrink-0"
                title={`Rated ${entry.rating}/5`}
                data-testid={`daily-plan-tblock-rating-${entry.id}`}>
            {"★".repeat(entry.rating)}
          </span>
        )}
        <span className="font-mono opacity-90 shrink-0">{formatHours(entry.hours, { unit: true })}</span>
      </div>
    </EntryHoverCard>
  );
}

// R64.5 · Rich hover card — Task health (age, due, priority, checklist),
// Client / Brand / Project + Assignee resolved to names, Rating stars,
// User note, Review comment. Reviewers see an inline star + comment

// Feb 2026 · Isolated submit button with local busy state — prevents the
// double-click race that made users think submits were "dropping".
// stopPropagation kills the hover-card outside-click so the card doesn't
// close mid-request either.
function SubmitEntryButton({ entry, onDone }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.stopPropagation(); e.preventDefault();
    if (busy || done) return;
    setBusy(true);
    try {
      await api.post(`/time-entries/${entry.id}/submit`,
                     { client_now: localWallClockIso() });
      setDone(true);
      toast.success("Submitted");
      onDone?.();
    } catch (err) {
      // Idempotent-ish: if the entry moved to submitted between our clicks
      // (e.g. reviewer already progressed it), treat that as success.
      const msg = String(err?.response?.data?.detail || "").toLowerCase();
      if (msg.includes("already") || err?.response?.status === 409) {
        setDone(true); toast.success("Already submitted"); onDone?.();
      } else {
        toast.error(apiErrorMessage(err, "Failed to submit"));
      }
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between gap-2">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {done ? "Submitted" : "Planned"}
      </span>
      <button type="button" onClick={submit} disabled={busy || done}
        className={`h-7 px-2 border text-[11px] rounded-none ${done ? "border-emerald-600 bg-emerald-600 text-white" : "border-primary bg-primary text-primary-foreground hover:bg-primary/90"} ${(busy || done) ? "opacity-70 cursor-not-allowed" : ""}`}
        data-testid={`daily-plan-hover-submit-${entry.id}`}>
        {done ? "✓ Submitted" : busy ? "Submitting…" : "Submit for review"}
      </button>
    </div>
  );
}

// action (backed by EntryReviewButton). Uses shadcn HoverCard.
function EntryHoverCard({ entry, canReview, onReviewed, children, forceOpen = false }) {
  const { formatHours } = useEffortFormat();
  // Feb 2026 · Perm gate for the Content-Calendar rows (Deliverable +
  // Topic). Shown only when the task was spawned from a CC item AND
  // the viewer has `content_calendar.view`.
  const { hasPerm } = useWorkspacePerms();
  const canSeeCC = hasPerm("content_calendar.view");
  const s = STATE_CHIP[entry.state] || STATE_CHIP.submitted;
  // Task age (days since created).
  const ageDays = entry.task_created_at
    ? Math.max(0, Math.floor((Date.now() - new Date(entry.task_created_at).getTime()) / 86400000))
    : null;
  // Due-date urgency chip.
  const dueDays = entry.task_due_date
    ? Math.floor((new Date(entry.task_due_date + "T00:00:00").getTime() - Date.now()) / 86400000)
    : null;
  const dueChip = dueDays == null ? null
    : dueDays < 0
      ? { text: `Overdue ${-dueDays}d`, cls: "border-rose-500 bg-rose-50 text-rose-800" }
      : dueDays === 0
        ? { text: "Due today", cls: "border-amber-500 bg-amber-50 text-amber-800" }
        : dueDays <= 3
          ? { text: `Due in ${dueDays}d`, cls: "border-amber-400 bg-amber-50 text-amber-700" }
          : { text: `Due ${entry.task_due_date}`, cls: "border-slate-300 bg-slate-50 text-slate-700" };
  const checklistPct = entry.task_checklist_total
    ? Math.round((entry.task_checklist_done / entry.task_checklist_total) * 100)
    : null;
  return (
    <HoverCard openDelay={120} closeDelay={80} open={forceOpen ? true : undefined}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent side="top" align="start" className="w-96 p-3 rounded-none z-[95]"
                        data-testid={`daily-plan-hover-${entry.id}`}>
        {/* R138az · show block date + day-of-week */}
        {entry.date && (
          <div className="text-[10px] uppercase tracking-widest text-primary bg-primary/5 border border-primary/30 px-1.5 py-0.5 mb-2 inline-block font-semibold"
               data-testid={`daily-plan-hover-date-${entry.id}`}>
            {formatDateLong(entry.date)}
            {entry.start_time && entry.end_time && ` · ${entry.start_time}–${entry.end_time}`}
          </div>
        )}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="font-semibold text-sm flex-1 min-w-0 break-words leading-tight">
            {entry.task_title || "Untitled task"}
          </div>
          <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border shrink-0 ${s}`}>
            {STATE_LABEL[entry.state] || entry.state}
          </span>
        </div>
        {entry.task_id && (
          <Link to={`/app/tasks?task=${entry.task_id}`}
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border border-primary/40 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground transition mb-2 rounded-none"
                data-testid={`daily-plan-hover-open-task-${entry.id}`}>
            <ExternalLink className="w-3 h-3" />
            Open task
          </Link>
        )}

        {/* Health badges row */}
        <div className="flex flex-wrap items-center gap-1 mb-2">
          {entry.task_priority && (
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-border bg-muted">
              {entry.task_priority}
            </span>
          )}
          {entry.task_status && (
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-primary/40 bg-primary/5 text-primary-foreground/80">
              {entry.task_status}
            </span>
          )}
          {ageDays != null && (
            <span className="text-[9px] px-1.5 py-0.5 border border-border text-muted-foreground"
                  data-testid={`daily-plan-hover-age-${entry.id}`}>
              Age {ageDays}d
            </span>
          )}
          {dueChip && (
            <span className={`text-[9px] px-1.5 py-0.5 border ${dueChip.cls}`}
                  data-testid={`daily-plan-hover-due-${entry.id}`}>
              {dueChip.text}
            </span>
          )}
          {checklistPct != null && (
            <span className="text-[9px] px-1.5 py-0.5 border border-emerald-400/60 bg-emerald-50 text-emerald-700 font-mono"
                  title={`${entry.task_checklist_done}/${entry.task_checklist_total} checklist items done`}>
              ✓ {checklistPct}%
            </span>
          )}
          {entry.billable != null && (
            <span className={`text-[9px] px-1.5 py-0.5 border ${entry.billable ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500"}`}>
              {entry.billable ? "billable" : "non-bill"}
            </span>
          )}
        </div>

        {/* Meta grid */}
        <dl className="grid grid-cols-3 gap-x-2 gap-y-1 text-[11px] mb-2">
          <dt className="text-muted-foreground">Time</dt>
          <dd className="col-span-2 font-mono">
            {entry.start_time && entry.end_time
              ? `${entry.start_time}–${entry.end_time} · ${formatHours(entry.hours, { unit: true })}`
              : `${formatHours(entry.hours, { unit: true })} (no times)`}
          </dd>
          {entry.assignee_name && (<><dt className="text-muted-foreground">Assignee</dt><dd className="col-span-2 truncate" data-testid={`daily-plan-hover-assignee-${entry.id}`}>{entry.assignee_name}</dd></>)}
          {entry.project_name && (<><dt className="text-muted-foreground">Project</dt><dd className="col-span-2 truncate">{entry.project_name}</dd></>)}
          {entry.brand_name && (<><dt className="text-muted-foreground">Brand</dt><dd className="col-span-2 truncate">{entry.brand_name}</dd></>)}
          {entry.client_name && (<><dt className="text-muted-foreground">Client</dt><dd className="col-span-2 truncate">{entry.client_name}</dd></>)}
          {entry.category && (<><dt className="text-muted-foreground">Category</dt><dd className="col-span-2 capitalize">{entry.category}</dd></>)}
          {/* Feb 2026 · Content-Calendar context, perm-gated. */}
          {canSeeCC && entry.cc_deliverable_name && (
            <>
              <dt className="text-muted-foreground">Deliverable</dt>
              <dd className="col-span-2 truncate font-medium" data-testid={`daily-plan-hover-cc-deliverable-${entry.id}`}>
                {entry.cc_deliverable_name}
              </dd>
            </>
          )}
          {canSeeCC && entry.cc_topic && (
            <>
              <dt className="text-muted-foreground">Topic</dt>
              <dd className={`col-span-2 break-words ${entry.cc_topic_linked ? "font-bold text-emerald-800" : "font-semibold italic text-muted-foreground"}`}
                   data-testid={`daily-plan-hover-cc-topic-${entry.id}`}>
                {entry.cc_topic}
              </dd>
            </>
          )}
          {entry.rating > 0 && (
            <><dt className="text-muted-foreground">Rating</dt>
              <dd className="col-span-2 text-amber-500 font-mono">
                {"★".repeat(entry.rating)}{"☆".repeat(5 - entry.rating)}
              </dd></>
          )}
        </dl>
        {entry.note && (
          <div className="mt-2 pt-2 border-t border-border/60">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">User note</div>
            <div className="text-xs whitespace-pre-wrap break-words">{entry.note}</div>
          </div>
        )}
        {entry.review_comment && (
          <div className="mt-2 pt-2 border-t border-border/60 bg-amber-50/60 -mx-3 px-3 py-1.5">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 mb-0.5">Reviewer comment</div>
            <div className="text-xs whitespace-pre-wrap break-words">{entry.review_comment}</div>
          </div>
        )}
        {/* R103 · Submit button on the hover card. Planned entries can be
            promoted to submitted right here so a manager doesn't have to
            navigate to the full timesheet.
            Feb 2026 · Added busy-state guard — users were rage-clicking the
            button (no feedback), racing two POSTs and seeing the second one
            fail with "already submitted", then reporting "the submit
            doesn't work" even though the first request went through. */}
        {entry.state === "planned" && (
          <SubmitEntryButton entry={entry} onDone={onReviewed} />
        )}
        {canReview && (entry.state === "submitted" || entry.state === "approved" || !entry.state) && (
          <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Reviewer</span>
            <EntryReviewButton entry={entry} onReviewed={onReviewed} />
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

function DayLane({ lane, hours, date, onDoubleAdd, canReview, onReviewed, density = "normal", checkinMarker = null, selectedEntryId = null, onSelectEntry = null }) {
  const { formatHours } = useEffortFormat();
  // Feb 2026 · Timeline block double-click → open the linked task in
  // the standard Edit Task popup (`/app/tasks?task=<id>` route param).
  const navigate = useNavigate();
  const entriesForDay = (lane.entries || []).filter((e) => e.date === date);
  const dayTotal = entriesForDay.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const laneHeight = (DENSITY_BLOCK[density] || DENSITY_BLOCK.normal).laneHeightPx;
  // Feb 2026 · R293 — Normal/Comfortable densities should let the text
  // wrap instead of truncating. Compact stays single-line to keep rows
  // short in dense views.
  const wrapBlockText = density !== "compact";
  // Feb 2026 · R293 — Lane-level leave/WFH badge: finds the chip(s) that
  // overlap the current `date` so a manager scanning the Daily Plan sees
  // "On leave" / "WFH" at a glance, next to the shift pill. Backend
  // populates `lane.leaves` with `{from_date, to_date, kind, leave_type,
  // half_day, note}` entries.
  const statusChips = (lane.leaves || []).filter(
    (l) => (l.from_date || "") <= date && (l.to_date || "") >= date,
  );
  const onLaneDblClick = (ev) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const frac = Math.max(0, Math.min(1, x / rect.width));
    const startHour = Math.round(START_HOUR + frac * HOURS_SPAN);
    onDoubleAdd?.({ userId: lane.user_id, start: `${String(startHour).padStart(2,"0")}:00`, taskId: null });
  };
  return (
    <div className="grid" style={{ gridTemplateColumns: `180px repeat(${hours.length}, minmax(60px, 1fr))` }}>
      <div className="border-b border-border/40 px-3 py-2 text-xs flex flex-col gap-0.5">
        <span className="font-medium truncate">{lane.user_name}</span>
        <span className="text-[10px] text-muted-foreground font-mono">{formatHours(dayTotal, { unit: true })}</span>
        {/* Oct 2026 · Shift window for the person. */}
        {lane.shift?.start_time && (
          <span className="text-[10px] text-muted-foreground/90 font-mono truncate"
                data-testid={`daily-plan-shift-${lane.user_id}`}>
            🕒 {lane.shift.start_time}–{lane.shift.end_time}
            {lane.shift.name && lane.shift.name !== "General" ? ` · ${lane.shift.name}` : ""}
          </span>
        )}
        {/* Feb 2026 · R293 — Leave / WFH chips when the viewed date is
            inside an approved absence window. "On leave" is amber,
            "WFH" is sky. Clicking the chip surfaces the note via
            native tooltip to keep the lane header compact. */}
        {statusChips.map((c, idx) => (
          <span key={idx}
                className={`inline-block w-fit text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border ${
                  c.kind === "wfh"
                    ? "bg-sky-50 border-sky-300 text-sky-700"
                    : "bg-amber-50 border-amber-300 text-amber-700"
                }`}
                title={c.note || `${c.kind === "wfh" ? "WFH" : c.leave_type || "Leave"}${c.half_day ? " (half)" : ""}`}
                data-testid={`daily-plan-status-${lane.user_id}-${c.kind}`}>
            {c.kind === "wfh" ? "WFH" : (c.half_day ? "On leave · ½" : "On leave")}
          </span>
        ))}
      </div>
      <div className="col-span-full relative border-b border-border/40"
           style={{ gridColumn: `2 / span ${hours.length}`, height: laneHeight }}
           onDoubleClick={onLaneDblClick}
           title="Double-click empty space to quick-add a planned block">
        {/* Feb 2026 · Shift-aware capacity band — paint the shift window
            in soft emerald and dim the hours outside so planned blocks
            that stray out of shift pop visually. The band sits behind
            the grid lines so the hour divisions stay readable. */}
        {(() => {
          const b = shiftBounds(lane.shift);
          if (!b) return null;
          const leftPct  = ((b.cs - START_HOUR) / HOURS_SPAN) * 100;
          const widthPct = ((b.ce - b.cs) / HOURS_SPAN) * 100;
          return (
            <>
              <div className="absolute inset-y-0 bg-neutral-200/40 dark:bg-neutral-800/40 pointer-events-none"
                   style={{ left: 0, width: `${leftPct}%` }}
                   data-testid={`daily-plan-shift-pre-${lane.user_id}`} />
              <div className="absolute inset-y-0 bg-emerald-100/60 dark:bg-emerald-900/20 pointer-events-none"
                   style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                   data-testid={`daily-plan-shift-band-${lane.user_id}`} />
              <div className="absolute inset-y-0 bg-neutral-200/40 dark:bg-neutral-800/40 pointer-events-none"
                   style={{ left: `${leftPct + widthPct}%`, right: 0 }}
                   data-testid={`daily-plan-shift-post-${lane.user_id}`} />
            </>
          );
        })()}
        {hours.map((h, i) => (
          <div key={h} className="absolute inset-y-0 border-l border-border/30" style={{ left: `${(i / hours.length) * 100}%` }} />
        ))}
        {entriesForDay.map((e) => {
          const start = parseHM(e.start_time);
          const end   = parseHM(e.end_time);
          const positioned = start != null && end != null && end > start && start >= START_HOUR && end <= END_HOUR;
          const leftPct = positioned ? ((start - START_HOUR) / HOURS_SPAN) * 100 : 0;
          const widthPct = positioned
            ? ((end - start) / HOURS_SPAN) * 100
            : Math.max(3, (e.hours / HOURS_SPAN) * 100);
          const s = STATE_BLOCK[e.state] || STATE_BLOCK.submitted;
          const outOfShift = positioned && isBlockOutsideShift(e, lane.shift);
          const isSelected = selectedEntryId === e.id;
          return (
            <EntryHoverCard key={e.id} entry={e} canReview={canReview} onReviewed={onReviewed} forceOpen={isSelected}>
              <div className={`absolute top-1 bottom-1 border overflow-hidden text-[10px] flex ${wrapBlockText ? "items-start flex-wrap" : "items-center"} gap-1 px-1.5 shadow-sm cursor-pointer ${s.bg} ${s.border} ${s.fg} ${outOfShift ? "ring-2 ring-amber-500 ring-offset-1 ring-offset-background" : ""} ${isSelected ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : ""}`}
                   style={{ left: `${leftPct}%`, width: `calc(${widthPct}% - 2px)` }}
                   title={outOfShift ? `Outside shift ${lane.shift?.start_time}–${lane.shift?.end_time}` : (e.task_id ? "Double-click to open task" : undefined)}
                   onClick={(ev) => { ev.stopPropagation(); onSelectEntry?.(isSelected ? null : e.id); }}
                   // Feb 2026 · Double-click opens the linked task in
                   // the standard Edit Task popup via the `?task=<id>`
                   // route param on the Tasks page. Silently no-ops
                   // when the block has no task_id (synthetic WFH /
                   // leave rows).
                   onDoubleClick={(ev) => {
                     ev.stopPropagation();
                     ev.preventDefault();
                     if (e.task_id) navigate(`/app/tasks?task=${encodeURIComponent(e.task_id)}`);
                   }}
                   data-testid={`daily-plan-block-${e.id}`}>
                <span className={`${wrapBlockText ? "whitespace-normal break-words" : "truncate"} flex-1 min-w-0 leading-tight`}>
                  {e.task_title || e.project_name || "Entry"}
                </span>
                {e.rating > 0 && (
                  <span className="text-amber-200 font-mono shrink-0"
                        title={`Rated ${e.rating}/5`}
                        data-testid={`daily-plan-block-rating-${e.id}`}>
                    {"★".repeat(e.rating)}
                  </span>
                )}
                <span className="font-mono opacity-90 shrink-0">{formatHours(e.hours, { unit: true })}</span>
              </div>
            </EntryHoverCard>
          );
        })}
        {/* Iter Feb2026 · Attendance markers layer (App = blue, Device = purple) */}
        {checkinMarker && (
          <CheckInMarkerRow marker={checkinMarker}
                             startHour={START_HOUR}
                             endHour={END_HOUR} />
        )}
      </div>
    </div>
  );
}

// ── WEEK view: 7 vertically-stacked mini day swimlanes ──────────────

function WeekView({ lanes, from, canReview, onReviewed }) {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(from, i)),
    [from],
  );
  return (
    <div className="space-y-3 p-2" data-testid="daily-plan-week">
      {days.map((d) => (
        <div key={d} className="border border-border" data-testid={`daily-plan-week-day-${d}`}>
          <div className="bg-muted/40 px-3 py-1.5 text-xs font-semibold flex items-center justify-between">
            <span>{dowShort(d)} · {d}</span>
            <WeekDayTotal lanes={lanes} date={d} />
          </div>
          <DaySwimlane lanes={lanes} date={d} canReview={canReview} onReviewed={onReviewed} />
        </div>
      ))}
    </div>
  );
}

function WeekDayTotal({ lanes, date }) {
  const { formatHours } = useEffortFormat();
  const total = lanes.reduce((s, l) => {
    return s + (l.entries || []).filter((e) => e.date === date).reduce((a, e) => a + (Number(e.hours) || 0), 0);
  }, 0);
  return <span className="font-mono text-muted-foreground">{formatHours(total, { unit: true })}</span>;
}

// ── MONTH view: calendar heatmap — rows=people, cols=days ───────────

// Feb 2026 R291r perf · MonthHeatmap + MonthRow (together ~140 lines
// with a HoverCard tree) are now in their own file so React.lazy
// splits them into a dedicated chunk. Users who stay on Day / Week /
// List never download the Month renderer.
// The import still resolves synchronously at module scope, so hoist
// it behind React.lazy at the top of this file (see import above).
// (original MonthHeatmap + MonthRow definitions removed here)



// ── QUICK ADD dialog ─────────────────────────────────────────────────
// Searches tasks (typeahead against /workspaces/{ws}/tasks?q=...) and
// creates a planned time-entry for the picked date + start/end. Opens
// on the Quick add button or via double-click on an empty swimlane slot.

function QuickAddPlanBlock({ wsId, initialDate, initialTaskId, initialStart, onClose, onAdded }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(initialDate || todayISO());
  const [start, setStart] = useState(initialStart || "09:00");
  const [end, setEnd] = useState(() => {
    const s = initialStart || "09:00";
    const [h, m] = s.split(":").map(Number);
    const eh = Math.min(21, h + 1);
    return `${String(eh).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Pre-fetch the task if an id was seeded.
  useEffect(() => {
    if (!initialTaskId || !wsId) return;
    api.get(`/workspaces/${wsId}/tasks/${initialTaskId}`)
       .then((r) => setSelected(r.data))
       .catch(() => {});
  }, [initialTaskId, wsId]);

  // Debounced typeahead search.
  useEffect(() => {
    if (!q || q.length < 2 || selected) { setResults([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get(`/workspaces/${wsId}/tasks`, { params: { q, limit: 10 } })
         .then((r) => setResults(r.data?.rows || r.data?.items || r.data || []))
         .catch(() => setResults([]))
         .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, wsId, selected]);

  const hours = useMemo(() => {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    return Math.max(0, ((eh * 60 + em) - (sh * 60 + sm)) / 60);
  }, [start, end]);

  const submit = async () => {
    if (!selected) { toast.error("Pick a task first"); return; }
    if (hours <= 0) { toast.error("End time must be after start"); return; }
    setSaving(true);
    try {
      await api.post(`/workspaces/${wsId}/time-entries`, {
        task_id: selected.id,
        project_id: selected.project_id,
        client_id: selected.client_id,
        date,
        start_time: start,
        end_time: end,
        hours: Number(hours.toFixed(2)),
        note,
        state: "planned",
        billable: !!selected.billable,
      });
      toast.success("Planned block added");
      onAdded?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Add failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="daily-plan-quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Quick add planned block
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!selected ? (
            <>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search tasks by title…"
                       className="h-9 rounded-none pl-7"
                       autoFocus
                       data-testid="daily-plan-quick-add-search" />
              </div>
              <div className="border border-border max-h-52 overflow-y-auto rounded-none">
                {searching && <div className="text-[11px] text-muted-foreground p-2">Searching…</div>}
                {!searching && q.length < 2 && (
                  <div className="text-[11px] text-muted-foreground p-2 italic">Type at least 2 characters to search tasks.</div>
                )}
                {!searching && q.length >= 2 && results.length === 0 && (
                  <div className="text-[11px] text-muted-foreground p-2 italic">No tasks match.</div>
                )}
                {results.map((t) => (
                  <button key={t.id} type="button"
                          onClick={() => setSelected(t)}
                          className="w-full text-left px-2 py-1.5 border-b border-border/40 hover:bg-primary/10 text-xs"
                          data-testid={`daily-plan-quick-add-result-${t.id}`}>
                    <div className="font-medium truncate">{t.title}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {t.project_name || "—"} · {t.status || "—"}
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="border border-primary/40 bg-primary/5 p-2 flex items-center gap-2" data-testid="daily-plan-quick-add-selected">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{selected.title}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {selected.project_name || "—"}
                </div>
              </div>
              <Button size="sm" variant="ghost" className="h-7 rounded-none text-[11px]"
                      onClick={() => { setSelected(null); setQ(""); }}
                      data-testid="daily-plan-quick-add-change">Change</Button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            <label className="text-[10px] text-muted-foreground">Date
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                     min={todayISO()}
                     className="h-8 rounded-none mt-0.5" data-testid="daily-plan-quick-add-date" />
            </label>
            <label className="text-[10px] text-muted-foreground">Start
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                     className="h-8 rounded-none mt-0.5" data-testid="daily-plan-quick-add-start" />
            </label>
            <label className="text-[10px] text-muted-foreground">End
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                     className="h-8 rounded-none mt-0.5" data-testid="daily-plan-quick-add-end" />
            </label>
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder="Note (optional)"
                 className="h-8 rounded-none" data-testid="daily-plan-quick-add-note" />
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground">Duration <span className="font-mono text-foreground">{hours.toFixed(2)}h</span></span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="rounded-none" onClick={onClose}
                      data-testid="daily-plan-quick-add-cancel">Cancel</Button>
              <Button size="sm" className="rounded-none" onClick={submit} disabled={saving || !selected || hours <= 0}
                      data-testid="daily-plan-quick-add-submit">
                {saving ? "Adding…" : "Add planned block"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// R104 · Flat sortable list of every timesheet entry across the visible
// date range. Reads from the already-filtered `lanes` so global search +
// multi-picks apply here too. Managers use this for quick scans /
// end-of-week reviews without dealing with the swimlane layout.
// Feb 2026 · Inline rating (1-5 stars) + reviewer comment editing per
// row, so approvers no longer need to open each entry's popover.
// R124 · Feb 2026 — User note column moved next to Task title, Wrap
// text toggle + user-resizable columns (drag the right edge of any
// column header). Column widths persist within the dialog session.
const LIST_COLUMNS = [
  { key: "date",           label: "Date",         width: 100, align: "left",  sortable: true },
  { key: "start_time",     label: "Start",        width: 70,  align: "left",  sortable: true },
  { key: "end_time",       label: "End",          width: 70,  align: "left",  sortable: true },
  { key: "hours",          label: "Hrs",          width: 60,  align: "left",  sortable: true },
  { key: "client_name",    label: "Client",       width: 130, align: "left",  sortable: true },
  { key: "brand_name",     label: "Brand",        width: 130, align: "left",  sortable: true },
  { key: "project_name",   label: "Project",      width: 170, align: "left",  sortable: true },
  { key: "task_title",     label: "Task",         width: 230, align: "left",  sortable: true },
  { key: "note",           label: "User note",    width: 240, align: "left",  sortable: false },
  { key: "assignee_name",  label: "Assignee",     width: 150, align: "left",  sortable: true },
  { key: "state",          label: "State",        width: 110, align: "left",  sortable: true },
  { key: "rating",         label: "Rating",       width: 120, align: "left",  sortable: false },
  { key: "reviewer_note",  label: "Reviewer note", width: 220, align: "left", sortable: false },
];

/* Feb 2026 R291s · ListView / MoveEntriesDialog / ListRow
   moved to DailyPlanListView.jsx so React.lazy can code-split
   them. Imported at the top of this file as `ListView`. */

